"use client";

import type { TrackPayload } from "@shared/lib/analytics";
import { currentConsent } from "@shared/lib/consent";

// ---------------------------------------------------------------------------
// track() côté navigateur : identifiant anonyme persistant (localStorage) +
// identifiant de session (sessionStorage), envoi via sendBeacon quand c'est
// possible pour ne pas perdre les événements au changement de page.
// Silencieux en cas d'échec : l'analytics ne doit jamais gêner l'utilisateur.
//
// Mesure d'audience de premier rang, exemptée de consentement tant qu'elle en
// respecte les conditions (CNIL) : identifiant renouvelé au bout de 13 mois,
// aucune transmission à des tiers, et opposition possible depuis le panneau
// « Gestion des cookies » (`lib/consent.ts`), qui coupe tout envoi.
// ---------------------------------------------------------------------------

// Clés de l'ancien nom du produit, gardées telles quelles : les renommer
// ferait de chaque visiteur connu un nouveau visiteur anonyme.
const ANONYMOUS_KEY = "sillage_aid";
const SESSION_KEY = "sillage_sid";
const ANONYMOUS_CREATED_KEY = "sillage_aid_at";
const ANONYMOUS_MAX_AGE_MS = 395 * 24 * 60 * 60 * 1000;

function readOrCreateId(storage: Storage, key: string) {
  try {
    let id = storage.getItem(key);
    if (!id) {
      id = crypto.randomUUID();
      storage.setItem(key, id);
    }
    return id;
  } catch {
    return undefined;
  }
}

/** Identifiant anonyme, renouvelé au-delà de 13 mois. */
function readOrCreateAnonymousId() {
  try {
    const storage = window.localStorage;
    const now = Date.now();
    const createdAt = Number(storage.getItem(ANONYMOUS_CREATED_KEY)) || 0;
    // Un identifiant antérieur à cette règle n'a pas de date : son délai part
    // d'aujourd'hui plutôt que d'en faire un nouveau visiteur.
    if (createdAt && now - createdAt > ANONYMOUS_MAX_AGE_MS) storage.removeItem(ANONYMOUS_KEY);
    if (!createdAt || now - createdAt > ANONYMOUS_MAX_AGE_MS) {
      storage.setItem(ANONYMOUS_CREATED_KEY, String(now));
    }
    return readOrCreateId(storage, ANONYMOUS_KEY);
  } catch {
    return undefined;
  }
}

/** Opposition aux statistiques internes : on oublie aussi les identifiants. */
export function forgetAnalyticsIds() {
  try {
    window.localStorage.removeItem(ANONYMOUS_KEY);
    window.localStorage.removeItem(ANONYMOUS_CREATED_KEY);
    window.sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // Stockage indisponible : rien à effacer.
  }
}

export function track(
  name: string,
  properties?: Record<string, string | number | boolean | null>,
) {
  if (typeof window === "undefined") return;
  if (currentConsent()?.internalStats === false) return;

  const payload: TrackPayload = {
    name,
    anonymousId: readOrCreateAnonymousId(),
    sessionId: readOrCreateId(window.sessionStorage, SESSION_KEY),
    path: window.location.pathname,
    referrer: document.referrer || undefined,
    properties,
  };

  const body = JSON.stringify(payload);
  try {
    if (navigator.sendBeacon?.("/api/track", new Blob([body], { type: "application/json" }))) {
      return;
    }
  } catch {
    // sendBeacon indisponible : on retombe sur fetch.
  }

  void fetch("/api/track", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {});
}
